import { useCallback, useEffect, useState } from 'react';
import { AlertCircle } from '../../icons';
// 채택 3단계 ② 인사 — 단추를 정본 Button 으로(D1).
import { Button } from '../../components/shared';
import {
  listProfileRequests, decideProfileRequest,
  REQUEST_STATUS_LABEL,
  type ProfileRequestListDto, type ProfileRequestDto, type RequestStatus,
} from '../../api/hr-profile-requests';
// 손짠 머리 → 정본 PageHeader · 경로 · 범위 칩(2026-10-17 · web 레인 W7a — lib/page-crumbs.ts)
import { PageHeader } from '../../components/shared';
import { crumbsFor, scopeFor } from '../../lib/page-crumbs';
import './ProfileRequestsPage.css';

/**
 * 정정 요청 — 담당자 화면 (시안 B2 의 짝, migration 136).
 *
 * ★★ **승인은 값을 바꾸지 않는다.** 이 화면에서 「승인」 을 눌러도 이름·직위는 그대로다 —
 *    승인은 「맞다, 처리하겠다」 는 대답이고, 실제 반영은 명부나 발령에서 한다.
 *    그래서 **행마다 «어디서 처리하는가» 가 미리 보이고**, 승인 뒤에도 그 말을 다시 띄운다.
 *    (누르고 나서 «아무 일도 안 일어났다» 가 되지 않게 — 인사발령·인사평가와 같은 규율.)
 * ★ **반려는 이유를 받는다.** 직원이 왜 안 되는지 모르면 같은 요청이 다시 온다.
 * ★ 지표는 **거르기와 무관하게 전체**를 본다 — 「대기 3건」 이 필터에 따라 바뀌면 안 된다.
 */

const FILTERS: ReadonlyArray<{ key: RequestStatus | 'ALL'; label: string }> = [
  { key: 'PENDING', label: '대기' },
  { key: 'APPROVED', label: '승인' },
  { key: 'REJECTED', label: '반려' },
  { key: 'CANCELED', label: '회수' },
  { key: 'ALL', label: '전체' },
];

export function ProfileRequestsPage() {
  const [list, setList] = useState<ProfileRequestListDto | null>(null);
  const [state, setState] = useState<'loading' | 'ok' | 'folded'>('loading');
  const [filter, setFilter] = useState<RequestStatus | 'ALL'>('PENDING');
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  /** 반려하려는 행 — 이유를 받기 전에는 보내지 않는다. */
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    setList(await listProfileRequests({
      ...(filter === 'ALL' ? {} : { status: filter }),
      search,
    }));
  }, [filter, search]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        await load();
        if (alive) setState('ok');
      } catch {
        if (alive) setState('folded');
      }
    })();
    return () => { alive = false; };
  }, [load]);

  const approve = async (r: ProfileRequestDto) => {
    setBusy(r.id);
    setError(null);
    setNotice(null);
    try {
      const res = await decideProfileRequest(r.id, true);
      // ★ 값은 바뀌지 않았다 — 다음 걸음을 그대로 옮긴다.
      setNotice(`${r.userName ?? ''} · ${r.fieldLabel} 요청을 승인했습니다. ${res.nextActionLabel}`);
      await load();
    } catch (e) {
      setError(msgOf(e) ?? '승인하지 못했습니다');
    } finally {
      setBusy(null);
    }
  };

  const reject = async (r: ProfileRequestDto) => {
    if (!note.trim()) { setError('반려 사유를 적어 주세요'); return; }
    setBusy(r.id);
    setError(null);
    setNotice(null);
    try {
      await decideProfileRequest(r.id, false, note.trim());
      setRejecting(null);
      setNote('');
      setNotice(`${r.userName ?? ''} · ${r.fieldLabel} 요청을 반려했습니다.`);
      await load();
    } catch (e) {
      setError(msgOf(e) ?? '반려하지 못했습니다');
    } finally {
      setBusy(null);
    }
  };

  const s = list?.summary;

  return (
    <div className="hrpage hrpcr">
      <PageHeader
        breadcrumb={crumbsFor('/erp/hr/profile-requests')}
        scope={scopeFor('/erp/hr/profile-requests')}
        title="정정 요청"
        description="직원이 「내 인사기록카드」 에서 올린 정정 요청을 처리합니다"
      />

      {state === 'folded' && (
        <div className="hrpage__note">정정 요청을 불러오지 못했습니다 — 서버가 아직 준비되지 않았을 수 있습니다.</div>
      )}

      {state !== 'folded' && (
        <>
          <div className="hrpage__note">
            <b>승인해도 값은 바뀌지 않습니다.</b> 승인은 「맞다, 처리하겠다」 는 대답이고,
            실제 반영은 직원 명부나 인사발령에서 합니다 — 행마다 어디서 처리하는지 적어 뒀습니다.
          </div>
          {error && <p className="hrcert__err" role="alert">{error}</p>}
          {notice && <p className="hrpcr__notice" role="status">{notice}</p>}

          {s && (
            <div className="hrpage__kpis">
              <div className="hrpage__kpi hrpcr__kpi--wait">
                <span className="hrpage__kpil">대기</span>
                <span className="hrpage__kpiv">{s.pending}<small>건</small></span>
                <span className="hrpage__kpid">
                  {s.pendingNeedsAssignment > 0
                    ? `발령이 필요한 것 ${s.pendingNeedsAssignment}건`
                    : '발령이 필요한 것은 없습니다'}
                </span>
              </div>
              <div className="hrpage__kpi">
                <span className="hrpage__kpil">승인</span>
                <span className="hrpage__kpiv">{s.approved}<small>건</small></span>
                <span className="hrpage__kpid">처리는 명부 · 발령에서</span>
              </div>
              <div className="hrpage__kpi">
                <span className="hrpage__kpil">반려</span>
                <span className="hrpage__kpiv">{s.rejected}<small>건</small></span>
                <span className="hrpage__kpid">사유가 요청자에게 보입니다</span>
              </div>
            </div>
          )}

          <section className="hrpage__panel">
            <h2 className="hrpage__panelt">
              요청
              <span className="hrpage__panels">{list?.total ?? 0}건</span>
            </h2>

            <div className="hrpcr__bar">
              <div className="hrcert__tabs" role="group" aria-label="거르기">
                {FILTERS.map((f) => (
                  <button
                    key={f.key} type="button"
                    className={`hrcert__tab${filter === f.key ? ' is-on' : ''}`}
                    onClick={() => { setFilter(f.key); setRejecting(null); }}
                    aria-pressed={filter === f.key}
                  >
                    {f.label}
                    {f.key === 'PENDING' && s && s.pending > 0 && (
                      <span className="hrcert__count">{s.pending}</span>
                    )}
                  </button>
                ))}
              </div>
              <input
                className="hrpcr__search" value={search} aria-label="이름 · 사번으로 찾기"
                placeholder="이름 · 사번으로 찾기"
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            {state === 'loading' ? (
              <p className="hrpage__empty">불러오는 중…</p>
            ) : !list || list.rows.length === 0 ? (
              <p className="hrpage__empty">
                {filter === 'PENDING' ? '처리할 요청이 없습니다.' : '해당하는 요청이 없습니다.'}
              </p>
            ) : (
              <table className="hrpage__tbl">
                <thead>
                  <tr>
                    <th>요청일</th><th>요청자</th><th>칸</th><th>지금</th><th>바꿔 달라는 값</th>
                    <th>처리</th><th className="hrpage__act">&nbsp;</th>
                  </tr>
                </thead>
                <tbody>
                  {list.rows.map((r) => (
                    <tr key={r.id} className={r.open ? 'is-overdue' : ''}>
                      <td className="hrpcr__nowrap">{r.createdAt.slice(0, 10)}</td>
                      <td>
                        {r.userName ?? '—'}
                        <span className="hrpage__dim"> · {r.employeeNumber ?? '—'}</span>
                      </td>
                      <td>{r.fieldLabel}</td>
                      <td className="hrpage__dim">{r.currentValue ?? '—'}</td>
                      <td>
                        {r.requestedValue}
                        {r.reason && <span className="hrpage__dim hrpcr__why"> — {r.reason}</span>}
                      </td>
                      <td>
                        {r.open ? (
                          <span className={`hrpage__pill hrpcr__act is-${r.nextAction.toLowerCase()}`}>
                            {r.nextAction === 'ASSIGNMENT' ? '발령으로' : r.nextAction === 'ROSTER' ? '명부에서' : '시스템 밖'}
                          </span>
                        ) : (
                          <>
                            <span className={`hrpage__pill hrpcr__st is-${r.status.toLowerCase()}`}>
                              {REQUEST_STATUS_LABEL[r.status]}
                            </span>
                            {r.decidedByName && <span className="hrpage__dim"> {r.decidedByName}</span>}
                            {r.decisionNote && <div className="hrpage__dim hrpcr__why">{r.decisionNote}</div>}
                          </>
                        )}
                      </td>
                      <td className="hrpage__act">
                        {r.open && (
                          rejecting === r.id ? (
                            <span className="hrpcr__reject">
                              <input
                                value={note} maxLength={200} placeholder="반려 사유"
                                aria-label="반려 사유"
                                onChange={(e) => setNote(e.target.value)}
                              />
                              <Button variant="quiet-danger" size="sm" onClick={() => reject(r)} disabled={busy === r.id}>
                                반려
                              </Button>
                              <Button variant="secondary" size="sm" onClick={() => { setRejecting(null); setNote(''); }}>
                                취소
                              </Button>
                            </span>
                          ) : (
                            <>
                              <Button variant="secondary" size="sm" onClick={() => approve(r)} disabled={busy === r.id}>
                                승인
                              </Button>
                              <Button
                                variant="quiet-danger"
                                size="sm"
                                onClick={() => { setRejecting(r.id); setNote(''); setError(null); }}
                              >
                                반려
                              </Button>
                            </>
                          )
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          {s && s.pendingNeedsAssignment > 0 && (
            <div className="hrasg__overdue" role="status">
              <AlertCircle size={16} aria-hidden />
              대기 중 <b>{s.pendingNeedsAssignment}건</b>은 승인해도 <b>인사발령</b>을 따로 세워야 합니다 —
              명부에서 손으로 고치면 그 사람의 발령 이력에 구멍이 납니다.
            </div>
          )}
        </>
      )}
    </div>
  );
}

function msgOf(e: unknown): string | null {
  return (e as { response?: { data?: { message?: string } } }).response?.data?.message ?? null;
}
